/**
 * CZNA-1034 — "is this person already in the base?", answered under the heading
 * while the cataloguer types.
 *
 * <p>Three rules this component exists to keep:
 *
 * <ul>
 *   <li><b>Silent unless it has something to say.</b> Nothing renders for zero
 *       candidates, and nothing renders while loading or after a failure. 90.1 %
 *       of active PERSONAL records have a name no other record shares (measured
 *       7. 9. 2026), so silence is the normal state and the hint keeps its
 *       signal.</li>
 *   <li><b>Never blocking.</b> Two people of the same name are legitimately two
 *       authorities; the cataloguer decides. Same reasoning as
 *       `DUPLICATE_HEADING` being a WARNING and never an error (CZNA-221).</li>
 *   <li><b>Says WHY it matched.</b> A candidate found through a variant heading
 *       shows that variant — otherwise `Barnabáš` finding `Barnabus` reads as a
 *       bug rather than as the answer it is.</li>
 * </ul>
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button, Icon } from '@folio/stripes/components';
var rowStyle = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
    flexWrap: 'wrap',
    padding: '0.25rem 0 0.25rem 0',
};
var listStyle = {
    margin: '0.25rem 0 0 1.5rem',
    padding: 0,
    listStyle: 'none',
};
var itemStyle = {
    padding: '0.1rem 0',
};
var variantStyle = {
    color: '#616161',
    marginLeft: '0.35rem',
};
var totalStyle = {
    color: '#616161',
    marginLeft: '1.5rem',
};
var SimilarHeadingsHint = function (_a) {
    var data = _a.data, isLoading = _a.isLoading, onCompare = _a.onCompare;
    var intl = useIntl();
    var _b = useState(false), expanded = _b[0], setExpanded = _b[1];
    // A courtesy lookup renders nothing until it has an answer worth reading. In
    // particular no spinner: a placeholder under the heading that flickers on
    // every keystroke is the noise this design is trying not to be.
    if (isLoading || !data || data.items.length === 0)
        return null;
    return (React.createElement("div", { style: rowStyle, "data-test-similar-headings": true },
        React.createElement(Icon, { icon: "info", size: "small" }),
        React.createElement("span", { "data-test-similar-headings-count": true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.count", values: { count: data.total } }),
            ' ',
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.checkDuplicate" })),
        React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: function () { return setExpanded(function (prev) { return !prev; }); }, "data-test-similar-headings-toggle": true }, intl.formatMessage({
            id: expanded
                ? 'ui-czna-authorities.proposalForm.similarHeadings.hide'
                : 'ui-czna-authorities.proposalForm.similarHeadings.show',
        })),
        expanded && (React.createElement(React.Fragment, null,
            React.createElement("ul", { style: listStyle, "data-test-similar-headings-list": true }, data.items.map(function (item) { return (React.createElement("li", { key: item.id, style: itemStyle },
                React.createElement(Link, { to: item.kind === 'proposal'
                        ? "/czna-authorities/proposals/".concat(item.id)
                        : "/czna-authorities/authorities/".concat(item.id) }, item.headingValue),
                item.kind === 'proposal' && (React.createElement("span", { style: variantStyle, "data-test-similar-heading-proposal": true },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.isProposal" }))),
                item.matchedHeading && item.matchedHeading !== item.headingValue && (React.createElement("span", { style: variantStyle },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.variant", values: { heading: item.matchedHeading } }))),
                onCompare && (React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: function () { return onCompare(item); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.similarHeadings.compareAria' }, { heading: item.headingValue }), "data-test-similar-heading-compare": true },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.compare" }))))); })),
            data.total > data.items.length && (React.createElement("div", { style: totalStyle, "data-test-similar-headings-total": true },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.showingFirst", values: { shown: data.items.length, total: data.capped ? "".concat(data.total, "+") : data.total } })))))));
};
export default SimilarHeadingsHint;
